// Mock database service using localStorage
import { v4 as uuidv4 } from 'uuid';

// Sample data
const initialData = {
  users: [
    {
      id: '1',
      name: 'Dr. John Smith',
      email: 'doctor@example.com',
      password: 'doctor123',
      role: 'doctor',
      specialty: 'Cardiology',
      availability: ['Monday', 'Wednesday', 'Friday'],
      bio: 'Experienced cardiologist with 15 years of practice',
      profileImage: 'https://randomuser.me/api/portraits/men/41.jpg'
    },
    {
      id: '2',
      name: 'Jane Doe',
      email: 'patient@example.com',
      password: 'patient123',
      role: 'patient',
      dateOfBirth: '1985-04-12',
      medicalHistory: 'No significant issues',
      profileImage: 'https://randomuser.me/api/portraits/women/32.jpg'
    },
    {
      id: '3',
      name: 'Dr. Sarah Johnson',
      email: 'doctor2@example.com',
      password: 'doctor123',
      role: 'doctor',
      specialty: 'Dermatology',
      availability: ['Tuesday', 'Thursday', 'Saturday'],
      bio: 'Board-certified dermatologist specializing in skin conditions',
      profileImage: 'https://randomuser.me/api/portraits/women/44.jpg'
    }
  ],
  appointments: [
    {
      id: '1',
      doctorId: '1',
      patientId: '2',
      date: '2025-05-10',
      time: '10:00 AM',
      status: 'confirmed',
      reason: 'Annual checkup'
    }
  ],
  doctors: [
    {
      id: '1',
      userId: '1',
      specialty: 'Cardiology',
      availability: ['Monday', 'Wednesday', 'Friday'],
      bio: 'Experienced cardiologist with 15 years of practice',
      ratings: 4.8
    },
    {
      id: '3',
      userId: '3',
      specialty: 'Dermatology',
      availability: ['Tuesday', 'Thursday', 'Saturday'],
      bio: 'Board-certified dermatologist specializing in skin conditions',
      ratings: 4.9
    }
  ],
  patients: [
    {
      id: '2',
      userId: '2',
      dateOfBirth: '1985-04-12',
      medicalHistory: 'No significant issues',
      allergies: 'None'
    }
  ]
};

// Initialize localStorage with sample data if it doesn't exist
const initializeDatabase = () => {
  const databases = ['users', 'appointments', 'doctors', 'patients'];
  databases.forEach(db => {
    if (!localStorage.getItem(db)) {
      localStorage.setItem(db, JSON.stringify(initialData[db]));
    }
  });
};

// Generate a JWT-like token (not real JWT, just for simulation)
const generateToken = (userId) => {
  return `mock-token-${userId}-${Date.now()}`;
};

// Get data from localStorage
const getCollection = (collection) => {
  const data = localStorage.getItem(collection);
  return data ? JSON.parse(data) : [];
};

// Save data to localStorage
const saveCollection = (collection, data) => {
  localStorage.setItem(collection, JSON.stringify(data));
};

// Mock database service
export const mockDbService = {
  // Initialize the database
  init: () => {
    initializeDatabase();
    return { success: true };
  },

  // Auth methods
  auth: {
    register: async (userData) => {
      const users = getCollection('users');
      
      // Check if email already exists
      if (users.find(user => user.email === userData.email)) {
        throw new Error('Email already in use');
      }
      
      // Create new user
      const newUser = {
        id: uuidv4(),
        ...userData
      };
      
      users.push(newUser);
      saveCollection('users', users);
      
      // Create in respective collection based on role
      if (userData.role === 'doctor') {
        const doctors = getCollection('doctors');
        doctors.push({
          id: uuidv4(),
          userId: newUser.id,
          specialty: userData.specialty || '',
          availability: userData.availability || [],
          bio: userData.bio || '',
          ratings: 0
        });
        saveCollection('doctors', doctors);
      } else if (userData.role === 'patient') {
        const patients = getCollection('patients');
        patients.push({
          id: uuidv4(),
          userId: newUser.id,
          dateOfBirth: userData.dateOfBirth || '',
          medicalHistory: userData.medicalHistory || '',
          allergies: userData.allergies || ''
        });
        saveCollection('patients', patients);
      }
      
      const token = generateToken(newUser.id);
      localStorage.setItem('token', token);
      
      const { password, ...userWithoutPassword } = newUser;
      return { user: userWithoutPassword, token };
    },
    
    login: async (email, password) => {
      const users = getCollection('users');
      const user = users.find(user => user.email === email && user.password === password);
      
      if (!user) {
        throw new Error('Invalid credentials');
      }
      
      const token = generateToken(user.id);
      localStorage.setItem('token', token);
      
      const { password: pwd, ...userWithoutPassword } = user;
      return { user: userWithoutPassword, token };
    },
    
    logout: async () => {
      localStorage.removeItem('token');
      return { success: true };
    },
    
    getCurrentUser: async () => {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Not authenticated');
      }
      
      // Extract user ID from mock token
      const userId = token.split('-')[1];
      const users = getCollection('users');
      const user = users.find(user => user.id === userId);
      
      if (!user) {
        throw new Error('User not found');
      }
      
      const { password, ...userWithoutPassword } = user;
      return userWithoutPassword;
    },
    
    updateProfile: async (userData) => {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Not authenticated');
      }
      
      const userId = token.split('-')[1];
      const users = getCollection('users');
      const userIndex = users.findIndex(user => user.id === userId);
      
      if (userIndex === -1) {
        throw new Error('User not found');
      }
      
      // Update user data
      users[userIndex] = { ...users[userIndex], ...userData };
      saveCollection('users', users);
      
      const { password, ...userWithoutPassword } = users[userIndex];
      return userWithoutPassword;
    }
  },
  
  // Doctor methods
  doctors: {
    getAllDoctors: async () => {
      const doctors = getCollection('doctors');
      const users = getCollection('users');
      
      return doctors.map(doctor => {
        const user = users.find(u => u.id === doctor.userId);
        const { password, ...userWithoutPassword } = user || {};
        return { ...doctor, ...userWithoutPassword };
      });
    },
    
    getDoctorById: async (doctorId) => {
      const doctors = getCollection('doctors');
      const doctor = doctors.find(d => d.id === doctorId);
      
      if (!doctor) {
        throw new Error('Doctor not found');
      }
      
      const users = getCollection('users');
      const user = users.find(u => u.id === doctor.userId);
      
      if (!user) {
        throw new Error('User not found');
      }
      
      const { password, ...userWithoutPassword } = user;
      return { ...doctor, ...userWithoutPassword };
    },
    
    searchDoctorsBySpecialty: async (specialty) => {
      const doctors = getCollection('doctors');
      const filteredDoctors = doctors.filter(d => 
        d.specialty.toLowerCase().includes(specialty.toLowerCase())
      );
      
      const users = getCollection('users');
      
      return filteredDoctors.map(doctor => {
        const user = users.find(u => u.id === doctor.userId);
        const { password, ...userWithoutPassword } = user || {};
        return { ...doctor, ...userWithoutPassword };
      });
    },
    
    updateDoctorProfile: async (profileData) => {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Not authenticated');
      }
      
      const userId = token.split('-')[1];
      const doctors = getCollection('doctors');
      const doctorIndex = doctors.findIndex(doctor => doctor.userId === userId);
      
      if (doctorIndex === -1) {
        throw new Error('Doctor not found');
      }
      
      // Update doctor data
      doctors[doctorIndex] = { ...doctors[doctorIndex], ...profileData };
      saveCollection('doctors', doctors);
      
      return doctors[doctorIndex];
    }
  },
  
  // Patient methods
  patients: {
    getPatientById: async (patientId) => {
      const patients = getCollection('patients');
      const patient = patients.find(p => p.id === patientId);
      
      if (!patient) {
        throw new Error('Patient not found');
      }
      
      const users = getCollection('users');
      const user = users.find(u => u.id === patient.userId);
      
      if (!user) {
        throw new Error('User not found');
      }
      
      const { password, ...userWithoutPassword } = user;
      return { ...patient, ...userWithoutPassword };
    },
    
    updatePatientProfile: async (profileData) => {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Not authenticated');
      }
      
      const userId = token.split('-')[1];
      const patients = getCollection('patients');
      const patientIndex = patients.findIndex(patient => patient.userId === userId);
      
      if (patientIndex === -1) {
        throw new Error('Patient not found');
      }
      
      // Update patient data
      patients[patientIndex] = { ...patients[patientIndex], ...profileData };
      saveCollection('patients', patients);
      
      return patients[patientIndex];
    },
    
    getAllPatients: async () => {
      const patients = getCollection('patients');
      const users = getCollection('users');
      
      return patients.map(patient => {
        const user = users.find(u => u.id === patient.userId);
        const { password, ...userWithoutPassword } = user || {};
        return { ...patient, ...userWithoutPassword };
      });
    }
  },
  
  // Appointment methods
  appointments: {
    createAppointment: async (appointmentData) => {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Not authenticated');
      }
      
      const userId = token.split('-')[1];
      const users = getCollection('users');
      const user = users.find(u => u.id === userId);
      
      if (!user) {
        throw new Error('User not found');
      }
      
      const appointments = getCollection('appointments');
      const newAppointment = {
        id: uuidv4(),
        patientId: user.role === 'patient' ? userId : appointmentData.patientId,
        ...appointmentData,
        status: 'pending'
      };
      
      appointments.push(newAppointment);
      saveCollection('appointments', appointments);
      
      return newAppointment;
    },
    
    getPatientAppointments: async () => {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Not authenticated');
      }
      
      const userId = token.split('-')[1];
      const users = getCollection('users');
      const user = users.find(u => u.id === userId);
      
      if (!user || user.role !== 'patient') {
        throw new Error('Not authorized');
      }
      
      const patients = getCollection('patients');
      const patient = patients.find(p => p.userId === userId);
      
      if (!patient) {
        throw new Error('Patient not found');
      }
      
      const appointments = getCollection('appointments');
      return appointments.filter(a => a.patientId === patient.id);
    },
    
    getDoctorAppointments: async () => {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Not authenticated');
      }
      
      const userId = token.split('-')[1];
      const users = getCollection('users');
      const user = users.find(u => u.id === userId);
      
      if (!user || user.role !== 'doctor') {
        throw new Error('Not authorized');
      }
      
      const doctors = getCollection('doctors');
      const doctor = doctors.find(d => d.userId === userId);
      
      if (!doctor) {
        throw new Error('Doctor not found');
      }
      
      const appointments = getCollection('appointments');
      return appointments.filter(a => a.doctorId === doctor.id);
    },
    
    updateAppointmentStatus: async (appointmentId, status) => {
      const appointments = getCollection('appointments');
      const appointmentIndex = appointments.findIndex(a => a.id === appointmentId);
      
      if (appointmentIndex === -1) {
        throw new Error('Appointment not found');
      }
      
      appointments[appointmentIndex].status = status;
      saveCollection('appointments', appointments);
      
      return appointments[appointmentIndex];
    },
    
    cancelAppointment: async (appointmentId) => {
      const appointments = getCollection('appointments');
      const newAppointments = appointments.filter(a => a.id !== appointmentId);
      
      if (appointments.length === newAppointments.length) {
        throw new Error('Appointment not found');
      }
      
      saveCollection('appointments', newAppointments);
      
      return { success: true };
    },
    
    getAvailableTimeSlots: async (doctorId, date) => {
      // Mock implementation - generate random time slots
      const allTimeSlots = [
        '09:00 AM', '10:00 AM', '11:00 AM', 
        '01:00 PM', '02:00 PM', '03:00 PM', '04:00 PM'
      ];
      
      const appointments = getCollection('appointments');
      const bookedSlots = appointments
        .filter(a => a.doctorId === doctorId && a.date === date)
        .map(a => a.time);
      
      return allTimeSlots.filter(slot => !bookedSlots.includes(slot));
    }
  }
};

export default mockDbService;